import * as THREE from 'three';

import type { BooleanPosition } from '@/adapters/primitive/_contract/boolean/rod';
import type { Config } from '@/adapters/primitive/_contract/config';

export function toBooleanOperandOffset(config: Config, height: number): {
    lateral: number;
    depth: number;
} {
    const { operandSpreadAngleDegrees, resultChannelDepth } = config.boolean.display.layout;
    const gap = config.boolean.display.track.wheelEndGap;
    const radius = config.base.display.wheel.innerRadius + config.base.display.wheel.ringWidth;
    if (!Number.isFinite(operandSpreadAngleDegrees) || operandSpreadAngleDegrees <= 0 || operandSpreadAngleDegrees >= 90) {
        throw new Error('Boolean operand spread angle must be between 0 and 90 degrees.');
    }
    if (!Number.isFinite(resultChannelDepth) || resultChannelDepth <= 0 || !Number.isFinite(height)
        || !Number.isFinite(gap) || gap < 0 || !Number.isFinite(radius) || radius <= 0) {
        throw new Error('Boolean operand layout requires positive channel depth and wheel size, and non-negative wheel gap.');
    }
    const channelLength = Math.hypot(resultChannelDepth, height) - gap;
    const centreDistance = channelLength / 2 + gap;
    const planarDistanceSquared = centreDistance ** 2 - height ** 2;
    // The native disc radius does not shrink when workflow graph sections are fitted.
    const minimumLateral = radius + gap / 2;
    if (channelLength <= 0 || planarDistanceSquared <= minimumLateral ** 2) {
        throw new Error('Boolean Result channel is too short to separate Operand wheels while preserving equal segment lengths.');
    }
    const planarDistance = Math.sqrt(planarDistanceSquared);
    const angle = Math.max(
        operandSpreadAngleDegrees * Math.PI / 180,
        Math.asin(minimumLateral / planarDistance),
    );
    return {
        lateral: planarDistance * Math.sin(angle),
        depth: resultChannelDepth + planarDistance * Math.cos(angle),
    };
}

function sectionRotation(args: {
    pivot: BooleanPosition;
    from: BooleanPosition;
    to: BooleanPosition;
    progress: number;
}): THREE.Quaternion {
    const pivot = new THREE.Vector3(...args.pivot);
    const start = new THREE.Vector3(...args.from).sub(pivot);
    const finish = new THREE.Vector3(...args.to).sub(pivot);
    if (start.lengthSq() <= 0 || Math.abs(start.length() - finish.length()) > 1e-8) {
        throw new Error('Boolean rod rotation requires equal nonzero section lengths.');
    }
    const angle = start.angleTo(finish);
    if (angle <= 1e-10) return new THREE.Quaternion();
    const axis = new THREE.Vector3().crossVectors(start, finish);
    if (axis.lengthSq() <= 1e-16) axis.set(1, 0, 0);
    return new THREE.Quaternion().setFromAxisAngle(axis.normalize(), angle * Math.max(0, Math.min(1, args.progress)));
}

export function rotateBooleanSectionLateral(args: {
    pivot: BooleanPosition;
    from: BooleanPosition;
    to: BooleanPosition;
    progress: number;
    lateral: BooleanPosition;
}): [number, number, number] {
    const vector = new THREE.Vector3(...args.lateral).applyQuaternion(sectionRotation(args));
    return [vector.x, vector.y, vector.z];
}

export function rotateBooleanSection(args: {
    pivot: BooleanPosition;
    from: BooleanPosition;
    to: BooleanPosition;
    progress: number;
}): [number, number, number] {
    if (args.progress <= 0) return [...args.from];
    if (args.progress >= 1) return [...args.to];
    const pivot = new THREE.Vector3(...args.pivot);
    const point = new THREE.Vector3(...args.from).sub(pivot).applyQuaternion(sectionRotation(args)).add(pivot);
    return [point.x, point.y, point.z];
}

export function toBooleanOperationJunction(args: {
    inputOne: BooleanPosition;
    inputTwo?: BooleanPosition;
    output: BooleanPosition;
    height: number;
    wheelGap?: number;
}): [number, number, number] {
    const { inputOne, inputTwo, output, height } = args;
    const gap = args.wheelGap ?? 0;
    if (!Number.isFinite(gap) || gap < 0) throw new Error('Boolean segment wheel gap must be finite and non-negative.');
    if (inputTwo === undefined) {
        return [0, 0, (inputOne[2] + output[2]) / 2];
    }
    const inputDepth = inputOne[2];
    const outputDepth = output[2];
    const lateral = Math.abs(inputOne[0]);
    if (!Number.isFinite(lateral) || !Number.isFinite(height)
        || lateral <= 0 || inputTwo[0] !== -inputOne[0]
        || inputTwo[1] !== inputOne[1] || inputTwo[2] !== inputDepth
        || inputDepth <= outputDepth || output[0] !== 0 || output[1] !== 0) {
        throw new Error('Boolean path requires two symmetric operands in front of an on-axis Result.');
    }
    const difference = (depth: number) => 2 * (Math.hypot(lateral, height, inputDepth - depth) - gap)
        - (Math.hypot(height, outputDepth - depth) - gap);
    if (difference(inputDepth) >= 0) {
        throw new Error('Boolean path has no interior operation junction with equal operand and Result-half lengths.');
    }
    let front = inputDepth;
    let back = outputDepth;
    for (let iteration = 0; iteration < 64; iteration += 1) {
        const depth = (front + back) / 2;
        if (difference(depth) > 0) back = depth;
        else front = depth;
    }
    const depth = (front + back) / 2;
    if (Math.hypot(lateral, height, inputDepth - depth) <= gap
        || Math.hypot(height, outputDepth - depth) <= gap) {
        throw new Error('Boolean wheel gap must leave positive segment lengths.');
    }
    return [0, height, depth];
}

export function toBooleanSegmentEndpoint(wheel: BooleanPosition, junction: BooleanPosition, gap: number): [number, number, number] {
    const direction = new THREE.Vector3(...junction).sub(new THREE.Vector3(...wheel));
    if (!Number.isFinite(gap) || gap < 0 || direction.length() <= gap) {
        throw new Error('Boolean wheel gap must leave positive segment lengths.');
    }
    const endpoint = new THREE.Vector3(...wheel).addScaledVector(direction.normalize(), gap);
    return [endpoint.x, endpoint.y, endpoint.z];
}